import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type {
  TierProgramMatrixRow,
  TierProgramMatrixRowCreateInput,
} from "@colab/shared";

export type CreateRowInput = {
  programId: string;
  input: TierProgramMatrixRowCreateInput;
};

type CreateRowResponse = { id: string; row: TierProgramMatrixRow };

// POST /api/admin/tiers/programs/:programId/rows
//
// Pessimistic — matrix rows render with associated cells that key off the
// new row id, so synthetic-row optimism is more friction than it's worth.
export function useCreateTierRow() {
  const queryClient = useQueryClient();
  return useMutation<CreateRowResponse, ApiClientError, CreateRowInput>({
    mutationFn: ({ programId, input }) =>
      apiFetch<CreateRowResponse>(`/admin/tiers/programs/${programId}/rows`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: (_data, { programId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tiers.program(programId) });
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to add row");
    },
  });
}
